import { HubConnectionBuilder, HubConnection } from '@microsoft/signalr'
import * as signalR from '@microsoft/signalr'
import store from '@/store'
import { getModule } from 'vuex-module-decorators'
import authStore from '@/modules/auth/auth.store'
import sharedStore from '@/modules/shared/shared.store'
import { CancelError, CancellationToken } from '@/utils/cancel'
import getAPIEndpoint from '@/utils/endpoint'

let connection: HubConnection | null = null
let connecting = false
let realtimeEventTarget = new EventTarget()

export { realtimeEventTarget }

export function getRealtimeConnection(
  cancellationToken: CancellationToken | null = null
): Promise<HubConnection> {
  return new Promise(async (resolve, reject) => {
    setTimeout(async () => {
      // just resolve with the connection if we are connected
      if (connection) {
        resolve(connection)
        return
      }

      function onConnect() {
        if (cancellationToken && cancellationToken.cancelled) {
          reject(new CancelError())
        } else {
          resolve(connection!)
        }

        realtimeEventTarget.removeEventListener('connect', onConnect)
      }

      realtimeEventTarget.addEventListener('connect', onConnect)

      // fire a connection unless someone else is already doing that
      if (!connecting) {
        connecting = true

        const authModule = getModule(authStore, store)
        if (!authModule.getAuthToken) {
          await authModule.requestNewToken()
        }

        const token = authModule.getAuthToken

        let url = getAPIEndpoint() + '/realtime'
        let queryStr = []
        if (token) queryStr.push('access_token=' + encodeURIComponent(token))
        if (queryStr.length) url += '?' + queryStr.join('&')
        const conn = new HubConnectionBuilder()
          .withUrl(url)
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds() {
              return Math.random() * 5000
            }
          })
          .configureLogging(signalR.LogLevel.Debug)
          .build()

        conn.serverTimeoutInMilliseconds = 60 * 1000
        conn.keepAliveIntervalInMilliseconds = 20 * 1000

        conn.onreconnecting(error => {
          realtimeEventTarget.dispatchEvent(
            new CustomEvent<Error>('reconnecting', {
              detail: error
            })
          )
        })

        conn.onreconnected(connectionId => {
          realtimeEventTarget.dispatchEvent(
            new CustomEvent<string>('reconnected', {
              detail: connectionId
            })
          )

          window.location.reload()
        })

        conn
          .start()
          .then(() => {
            connection = conn
            realtimeEventTarget.dispatchEvent(new Event('connect'))
            connecting = false
          })
          .catch(err => {
            // clean up if the connection has problem
            connection = null
            connecting = false
            reject(err)
          })
      }
    }, 500)
  })
}

export async function closeRealtimeConnection() {
  const conn = connection
  connection = null
  connecting = false

  if (conn) await conn.stop()
}

export async function reconnectRealtimeConnection() {
  // do nothing if we haven't connect to realtime service
  if (!connection) {
    return
  }

  await closeRealtimeConnection()
  await getRealtimeConnection()
}
